import React, { useEffect, useState } from 'react';
import { FaTimes, FaTools } from 'react-icons/fa';
import { useForm } from 'react-hook-form';
import { InternalFieldName } from 'react-hook-form/dist/types/fields';
import { toast } from 'react-toastify';
import {
  InvoiceService,
  InvoiceViewModel,
} from '../../models/Invoice/InvoiceViewModel';
import { toMask } from '../../services/currency';
import { useAuth } from '../../contexts/auth';
import api from '../../services/api';
import InvoiceStatusState from '../../enums/InvoiceStatusState';

interface InvoiceServicesProps {
  invoice: InvoiceViewModel;
  items: InvoiceService[];
  refreshServices: () => void;
  removeService: (invoiceService: InvoiceService) => void;
}

type ServiceModel = {
  id: number;
  name: string;
  price: number;
};

interface InvoiceServiceFormFields {
  service: number;
  quantity: number;
  description?: string;
}

function addServerErrors<T>(
  errors: { [P in keyof T]?: string[] },
  setError: any
) {
  return Object.keys(errors).forEach((key) => {
    setError(key as keyof T, {
      type: 'server',
      message: errors[key as keyof T]!.join('. '),
    });
  });
}

export default function InvoiceServices({
  invoice,
  items,
  refreshServices,
  removeService,
}: InvoiceServicesProps) {
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
    reset,
  } = useForm<InvoiceServiceFormFields>({ mode: 'onBlur' });

  const [isLoading, setIsLoading] = useState(true);
  const [formErrors, setFormErrors] = useState<Record<string, any>>([]);
  const [services, setServices] = useState<ServiceModel[]>([]);

  const {
    user: { organization },
    isAdmin,
  } = useAuth();

  useEffect(() => {
    let shouldFocus = true;
    Object.keys(formErrors || {}).forEach((field: InternalFieldName) => {
      addServerErrors(formErrors, setError);

      shouldFocus = false;
    });
  }, [formErrors]);

  async function getServices() {
    if (organization) {
      setIsLoading(true);
      const response = await api.get(
        `organizations/${organization.id}/invoices/${invoice.id}/services/available`
      );
      // TODO: Remover os que já estão adicionados
      setServices(response.data.data);
      setIsLoading(false);
    }
  }

  const onSubmit = async (data: InvoiceServiceFormFields) => {
    try {
      await api.post(
        `organizations/${organization.id}/invoices/${invoice.id}/services`,
        data
      );

      setServices([]);
      refreshServices();
    } catch (e: any) {
      if (e?.response?.data?.errors) {
        if (e?.response?.status === 422) {
          setFormErrors(e.response.data.errors);
        } else {
          toast.error(
            e?.response?.data?.errors.reduce(
              (acc: string, error: string) => acc + error,
              ''
            )
          );
        }
      } else {
        throw e;
      }
    }
  };

  return (
    <>
      <span className='h2'>Serviços</span>
      <div className='table-responsive'>
        <table className='table'>
          <thead>
            <tr>
              <th>Nome</th>
              <th>Descrição</th>
              <th>Quantidade</th>
              {isAdmin && (
                <>
                  <th className='text-end'>Valor</th>
                  <th className='text-end'>Total</th>
                </>
              )}
              <th style={{ width: '20px' }}>&nbsp;</th>
            </tr>
          </thead>
          <tbody>
            {items.length > 0 ? (
              items.map((item) => (
                <tr key={item.id}>
                  <td>{item.name}</td>
                  <td>{item.description ?? 'Sem descrição'}</td>
                  <td>{item.quantity}</td>
                  {isAdmin && (
                    <>
                      <td className='text-end'>{toMask(item.price)}</td>
                      <td className='text-end'>
                        {toMask(item.quantity * item.price)}
                      </td>
                    </>
                  )}
                  <td>
                    {isAdmin &&
                      invoice.statusState !== InvoiceStatusState.Canceled &&
                      invoice.statusState !== InvoiceStatusState.Completed && (
                        <button
                          className='btn btn-danger btn-sm'
                          type='button'
                          onClick={() => removeService(item)}
                        >
                          <FaTimes />
                        </button>
                      )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={isAdmin ? 5 : 3}>Nenhum registro</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className='text-end'>
        {isAdmin &&
          invoice.statusState !== InvoiceStatusState.Canceled &&
          invoice.statusState !== InvoiceStatusState.Completed && (
            <button
              type='button'
              className='btn btn-secondary btn-sm'
              data-bs-toggle='modal'
              data-bs-target='#invoice-service-modal'
              onClick={() => getServices()}
            >
              Adicionar serviço <FaTools />
            </button>
          )}
        {/* Modal */}
        <div
          className='modal'
          tabIndex={-1}
          id='invoice-service-modal'
          data-bs-backdrop='static'
          data-bs-keyboard='false'
          aria-labelledby='staticBackdropLabel'
          aria-hidden='true'
        >
          <div className='modal-dialog'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>Adicionar serviço</h5>
                <button
                  type='button'
                  className='btn-close'
                  data-bs-dismiss='modal'
                  aria-label='Close'
                />
              </div>
              <div className='modal-body text-start'>
                <form
                  style={{
                    paddingTop: '1rem',
                    paddingBottom: '1rem',
                  }}
                  onSubmit={handleSubmit(onSubmit)}
                >
                  <div className='mb-3'>
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label className='form-label'>Selecione o serviço</label>
                    <select
                      required
                      className='form-select'
                      aria-label='Selecione o serviço'
                      {...register('service', { required: true })}
                    >
                      <option value=''>Selecione o serviço</option>
                      {services.map((service) => (
                        <option value={service.id} key={service.id}>
                          {service.name}
                        </option>
                      ))}
                    </select>
                    {errors.service && (
                      <div className='form-text text-danger'>
                        {errors.service.message}
                      </div>
                    )}
                  </div>
                  <div className='mb-3'>
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label htmlFor='inputLabel' className='form-label'>
                      Quantidade
                    </label>
                    <input
                      {...register('quantity', { required: true })}
                      className='form-control'
                      placeholder='Quantidade'
                      type='number'
                      min='1'
                      step='1'
                      required
                    />
                    {errors.quantity && (
                      <div className='form-text text-danger'>
                        {errors.quantity.message}
                      </div>
                    )}
                  </div>
                  <div className='mb-3'>
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label htmlFor='inputLabel' className='form-label'>
                      Descrição
                    </label>
                    <textarea
                      {...register('description')}
                      className='form-control'
                      placeholder='Descrição'
                    />
                    {errors.description && (
                      <div className='form-text text-danger'>
                        {errors.description.message}
                      </div>
                    )}
                  </div>
                  <div className='text-end mt-3'>
                    <button
                      type='submit'
                      className='btn btn-primary'
                      data-bs-dismiss='modal'
                    >
                      Adicionar
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
